// Pure helpers + shared constants for the Driver PWA.
// Extracted from DriverApp.js to keep the main file smaller.

export const STATUS_MAP = {
  in_attesa: { label: "Presa in Carico", color: "bg-zinc-500", text: "text-zinc-700", bg: "bg-zinc-100" },
  ritirato: { label: "Ritirato", color: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-100" },
  in_transito: { label: "In Transito", color: "bg-orange-500", text: "text-orange-700", bg: "bg-orange-100" },
  in_consegna: { label: "In Consegna", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-100" },
  consegnato: { label: "Consegnato", color: "bg-green-500", text: "text-green-700", bg: "bg-green-100" },
};

export const sLabel = (s) => STATUS_MAP[s]?.label || s;
export const sColor = (s) => STATUS_MAP[s]?.color || "bg-zinc-500";

export const ANOMALY_TYPES = [
  "Nessuno in sede",
  "Indirizzo errato",
  "Merce danneggiata",
  "Rifiuto merce",
  "Accesso impossibile",
  "Altro",
];

export const PICKED_UP_STATUSES = ["ritirato", "in_transito", "in_consegna", "consegnato"];

export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function mapsUrl(details, originCoords = null) {
  if (!details) return null;
  const dest = encodeURIComponent([details.address, details.cap, details.city, details.province, "Italia"].filter(Boolean).join(", "));
  const base = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
  if (originCoords && Array.isArray(originCoords) && originCoords.length === 2) {
    return `${base}&origin=${originCoords[0]},${originCoords[1]}`;
  }
  return base;
}

export function normalizeTrackingCode(rawText) {
  const value = (rawText || "").trim().toUpperCase();
  const colloMatch = value.match(/^([A-Z]{2}-\d+)-\d+$/);
  return colloMatch ? colloMatch[1] : value;
}

export function isPickedUp(job) {
  return !!job && PICKED_UP_STATUSES.includes(job.status);
}

export function getPrimaryStopDetails(job) {
  return isPickedUp(job) ? job?.dest_details : job?.origin_details;
}

export function getSecondaryStopDetails(job) {
  return isPickedUp(job) ? job?.origin_details : job?.dest_details;
}

export function getPrimaryStopCoords(job) {
  if (!job) return null;
  const coords = isPickedUp(job) ? job.dest_coords : job.origin_coords;
  return coords && coords.length === 2 ? coords : null;
}

export function getPrimaryStopSummary(job) {
  const details = getPrimaryStopDetails(job);
  const pickedUp = isPickedUp(job);
  const fallbackCity = pickedUp ? job?.destination : job?.origin;

  const companyOrName = details?.company || details?.name || "";
  const address = details?.address || "";
  const locality = [details?.cap, details?.city || fallbackCity, details?.province ? `(${details.province})` : ""]
    .filter(Boolean)
    .join(" ");

  return {
    // Line 1: company (or person name)
    title: companyOrName || address || fallbackCity || (pickedUp ? "Destinazione non disponibile" : "Ritiro non disponibile"),
    // Line 2: street address
    address: address || "",
    // Line 3: locality
    locality: locality || fallbackCity || "-",
    // Deprecated (kept for backward compat): optional person name if company is shown
    subtitle: details?.company && details?.name ? details.name : "",
    pickedUp,
    actionLabel: pickedUp ? "DA CONSEGNARE" : "DA RITIRARE",
  };
}

export function downloadPdf(file) {
  const binary = atob(file.data_base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const blob = new Blob([bytes], { type: file.content_type || "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export function getReadyBadgeLabel(item) {
  if (item?.goods_ready_status !== "pronta") return null;
  return item?.goods_ready_time ? `Pronta alle ${item.goods_ready_time}` : "Pronta";
}

export function getDdtBadgeLabel(item) {
  const count = item?.ddt_files?.length || 0;
  if (!count) return null;
  return count > 1 ? `DDT PDF (${count})` : "DDT PDF";
}

export function urlBase64ToUint8Array(b64) {
  const p = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + p).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function compressImage(file, maxW = 800, quality = 0.5) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ratio = Math.min(maxW / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Format an ISO timestamp (assumed UTC) to Italian local time HH:mm.
export function formatTimeIT(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  } catch {
    return "";
  }
}

// Format an ISO timestamp (assumed UTC) to Italian local datetime: dd/MM/yyyy HH:mm.
export function formatDateTimeIT(isoString) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome",
    });
  } catch {
    return "";
  }
}

// Returns true if the job is flagged as urgent in the DB (admin field "urgency")
export function isUrgent(job) {
  return (job?.urgency || "").toLowerCase() === "urgente";
}

